import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Circle, ArrowDown } from "lucide-react";

const API = `${window.location.origin}/api`;

// Mappa colore per pipeline / status. Coerente con la dashboard.
const PIPELINE_BADGE = {
  clinico: { label: "Clinico", cls: "bg-emerald-100 text-emerald-700" },
  orto: { label: "Orto", cls: "bg-blue-100 text-blue-700" },
};

const STATUS_BADGE = {
  open: { label: "Aperta", cls: "bg-amber-100 text-amber-700" },
  won: { label: "Vinta", cls: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Persa", cls: "bg-rose-100 text-rose-700" },
  abandoned: { label: "Abbandonata", cls: "bg-slate-100 text-slate-600" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function durationHumanized(fromIso, toIso) {
  if (!fromIso) return "";
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const ms = Math.max(0, to - from);
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "giorno" : "giorni"}`;
}

// Standalone timeline component. Pass either `oppId` (fa la fetch) o
// `timeline` (array già pronto). Mostra una stack verticale di stage con
// data di ingresso, durata di permanenza e badge "in corso".
export default function OppStageTimeline({ oppId, timeline: timelineProp }) {
  const [loading, setLoading] = useState(!timelineProp);
  const [timeline, setTimeline] = useState(timelineProp || []);
  const [error, setError] = useState("");

  useEffect(() => {
    if (timelineProp) {
      setTimeline(timelineProp);
      setLoading(false);
      return;
    }
    if (!oppId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/dashboard/opp-history/${oppId}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setTimeline(res.data?.timeline || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Errore caricamento storico");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [oppId, timelineProp]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento storico…
      </div>
    );
  }
  if (error) {
    return <div className="text-rose-600 text-sm p-4">{error}</div>;
  }
  if (timeline.length === 0) {
    return (
      <div className="text-slate-500 text-sm p-4 italic">
        Nessuna transizione registrata ancora per questa opportunità.
      </div>
    );
  }

  return (
    <ol className="relative" data-testid="opp-stage-timeline">
      {timeline.map((step, idx) => {
        const isCurrent = step.current;
        const pipelineInfo = PIPELINE_BADGE[step.pipeline_tag] || null;
        const statusInfo = STATUS_BADGE[step.status] || null;
        return (
          <li key={`${step.stage_id}-${step.entered_at}-${idx}`} className="relative pb-5 pl-8">
            {/* connector line */}
            {idx !== timeline.length - 1 && (
              <span className="absolute left-[10px] top-6 bottom-0 w-px bg-slate-200" aria-hidden />
            )}
            {/* dot */}
            <span
              className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center ${
                isCurrent ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >
              {isCurrent ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}
            </span>
            <div className="flex items-baseline flex-wrap gap-2">
              <span className="font-bold text-sm text-slate-800">
                {step.stage_name || "(stage senza nome)"}
              </span>
              {pipelineInfo && (
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${pipelineInfo.cls}`}>
                  {pipelineInfo.label}
                </span>
              )}
              {statusInfo && (
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${statusInfo.cls}`}>
                  {statusInfo.label}
                </span>
              )}
              {isCurrent && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                  In corso
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Ingresso: <span className="text-slate-700">{fmtDate(step.entered_at)}</span>
              {step.exited_at && (
                <>
                  {" "}· Uscita:{" "}
                  <span className="text-slate-700">{fmtDate(step.exited_at)}</span>
                </>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
              <ArrowDown className="w-3 h-3" /> Permanenza: {durationHumanized(step.entered_at, step.exited_at)}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
